// Type constraint for items that can be merged by label
import { groupBy, isEmpty } from 'lodash';
import { recalculateWeights } from './recalculateWeights';

export type InvestmentItem = {
  label: string;
  weight: number;
  allocation: number;
  children?: InvestmentItem[];
};

/**
 * Merges an array of items by their label property, summing weight and allocation values.
 *
 * @template T - A type that extends InvestmentItem
 * @param items - Array of items to merge
 * @returns A new array with items merged by label, where weight and allocation are summed
 *
 * @example
 * ```typescript
 * const items = [
 *   { label: 'A', weight: 10, allocation: 5, children: [] },
 *   { label: 'A', weight: 15, allocation: 8, children: [...] }
 * ];
 * mergeInvestmentsByLabel(items); // [{ label: 'A', weight: 25, allocation: 13 }]
 * ```
 */
export const mergeInvestmentsByLabel = <T extends InvestmentItem>(items: T[]): T[] =>
  recalculateWeights(mergeInvestmentsByLabelInternal(items));

const mergeInvestmentsByLabelInternal = <T extends InvestmentItem>(items: T[]): T[] => {
  const groups = groupBy(items, (item: InvestmentItem) => item.label);

  const result: T[] = [];
  items.forEach((item) => {
    const group = groups[item.label];
    if (!group) {
      return;
    }
    delete groups[item.label];

    const rowAllocation = group.map((x) => x.allocation).reduce((a, b) => a + b, 0);
    const children = group.flatMap((x) => x.children).filter((x) => x !== undefined);

    result.push({
      ...item,
      allocation: rowAllocation,
      children: !isEmpty(children) ? mergeInvestmentsByLabelInternal(children) : undefined,
    } as T);
  });
  return result;
};
